/**
 * 版本修改记录：把自动保存、后台写入和同步记录合成「编辑会话」，像 git log 一样一条一个提交。
 *
 * 实时保存每停手 1.5 秒存一次，原样列出来一分钟能有十几条、同一个字段 1 → 12 → 120 → 1200
 * 一路排开。人要看的是「谁、什么时候、最后把什么改成了什么」，所以：
 * - 同一个人、相邻两次保存间隔不超过 gap，算一个会话
 * - 会话内同一位置只留最初的旧值和最后的新值；改回原样的整条去掉；先加后删的也去掉
 */
import { configModule } from "../../../src/domain/config-modules.ts";
import type { Change } from "./autosave.ts";
import type { AuditEntry } from "./api.ts";

export interface ContentNotice { kind: "write" | "sync"; action: string; target: string; sourceA0?: string; sourceVersion?: number }
export interface LogEntry { id?: number; actor: string; atMs: number; changes: Change[]; notices?: ContentNotice[]; undo?: { sourceIds: number[]; module?: string; sourceVersion?: number } }
export interface NetChange extends Change { }
export interface Session { actor: string; fromMs: number; toMs: number; saves: number; rows: NetChange[]; notices: ContentNotice[]; truncated: boolean; entries: LogEntry[]; reverted: boolean }

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** entries 按时间倒序进来（接口就是这么给的），会话也按倒序出去 */
export function groupSessions(entries: Array<LogEntry & { truncated?: boolean }>, gapMs = 5 * 60_000): Session[] {
  const asc = [...entries].sort((a, b) => a.atMs - b.atMs || (a.id ?? 0) - (b.id ?? 0));
  const groups: Array<typeof asc> = [];
  for (const e of asc) {
    const g = groups[groups.length - 1];
    const last = g?.[g.length - 1];
    if (last && !last.undo && !e.undo && last.actor === e.actor && e.atMs - last.atMs <= gapMs) g.push(e);
    else groups.push([e]);
  }
  return groups.map((g) => ({
    actor: g[0].actor, fromMs: g[0].atMs, toMs: g[g.length - 1].atMs, saves: g.length,
    rows: netChanges(g.map((e) => e.changes.map(normalizeLegacy))),
    entries: g.map((e) => ({ ...e, changes: e.changes.map(normalizeLegacy) })),
    reverted: g.some((e) => e.changes.length > 0) && netChanges(g.map((e) => e.changes.map(normalizeLegacy))).length === 0, truncated: g.some((e) => e.truncated),
    notices: [...new Map(g.flatMap((e) => (e.notices ?? []).map((n) => [n.kind === "sync" ? `sync|${n.sourceA0}|${n.sourceVersion}` : `${n.action}|${n.target}`, n] as const))).values()],
  })).filter((s) => s.rows.length > 0 || s.notices.length > 0 || s.reverted).reverse();
}

/**
 * 老前端记下的改动没有 op / row：整行新增记成「`[id=16]` 从空变成一整行」，长值还被截成了字符串。
 * 按形状补回来：行级路径、一头是空另一头有值 → 新增 / 删除；行名从值里认（对象取 name，截断的 JSON 串用正则捞）
 */
export function normalizeLegacy(c: Change): Change {
  if (c.op || !/^\[[^\]]+\]$/.test(c.path)) return c;
  const empty = (v: unknown) => v === undefined || v === null || v === "";
  const op = empty(c.from) && !empty(c.to) ? "add" : !empty(c.from) && empty(c.to) ? "remove" : undefined;
  if (!op) return c;
  const v = op === "add" ? c.to : c.from;
  let row = c.row;
  if (!row && v && typeof v === "object") { const n = (v as Record<string, unknown>).name; if (typeof n === "string" && n) row = n; }
  if (!row && typeof v === "string") { const m = /"name":"([^"]+)"/.exec(v); if (m) row = m[1]; }
  const out: Change = { ...c, op };
  if (op === "add") delete out.from; else delete out.to;
  if (row) out.row = row;
  return out;
}

/** 一串按时间正序的改动 → 每个位置一条净改动 */
export function netChanges(batches: Change[][]): NetChange[] {
  const byKey = new Map<string, NetChange>();
  for (const batch of batches) {
    for (const c of batch) {
      const key = `${c.block}|${c.path}`;
      const prev = byKey.get(key);
      if (!prev) { byKey.set(key, { ...c }); continue; }
      // 位置第一次出现时的旧值留着；新值、行名取最新
      const next: NetChange = { ...prev, to: c.to };
      if (c.row ?? prev.row) next.row = c.row ?? prev.row;
      if (!("to" in c)) delete next.to;
      if (prev.op === "add" && c.op === "remove") { byKey.delete(key); continue; }
      if (prev.op === "remove" && c.op === "add") delete next.op; // 删了又加回来 = 改了这一行
      else if (c.op === "add" || prev.op === "add") next.op = "add";
      byKey.set(key, next);
    }
  }
  // 新增行下面后来又改的字段（传封面、填底图）照样列出来：新增那一行只写行名，细节看下面几行
  return [...byKey.values()].filter((c) => c.op || !same(c.from, c.to));
}

// 与服务端 src/admin/audit-content.ts 的 CONTENT_WRITE_SQL 同一口径（版本行「改过」的人按它算），改一边要改另一边
const CONTENT_TARGET = /^\/admin\/api\/config\/versions\/\d+\/(?!(?:publish|activate|unfreeze|check|snapshot|restore)$|auctions\/new-period$|merge\/orders\/preview$)[^?]+$/;
const WRITE_ACTION = /^(?:POST|PUT|PATCH|DELETE) \/admin\/api\/config\/versions\//;
const LEGACY_BLOCKS: Record<string, string> = { cfg_main_rival: "rivals", cfg_main_rival_cost: "rivalCells", cfg_pettown_rival_price: "pettownRivalPrices" };
export const auditBlockName = (block: string) => LEGACY_BLOCKS[block] ?? block;
function detailedChanges(e: AuditEntry): Change[] {
  const changes = e.detail?.changes;
  return Array.isArray(changes) ? changes.filter((c): c is Change => !!c && typeof c === "object" && typeof c.block === "string" && typeof c.path === "string").map((c) => ({ ...c, block: auditBlockName(c.block) })) : [];
}

function moduleUndoSource(value: object) {
  const meta = value as { selection?: { module?: unknown }; sourceVersion?: unknown };
  return typeof meta.selection?.module === "string" && configModule(meta.selection.module) && typeof meta.sourceVersion === "number" && Number.isSafeInteger(meta.sourceVersion) && meta.sourceVersion > 0
    ? { module: meta.selection.module, sourceVersion: meta.sourceVersion } : {};
}

/** 后台只记成功写入；缺字段明细的记录以保存摘要显示。 */
export function contentLogEntries(items: AuditEntry[]): Array<LogEntry & { truncated?: boolean }> {
  const out: Array<LogEntry & { truncated?: boolean }> = [];
  for (const e of items) {
    const changes = detailedChanges(e);
    const sync = e.action === "config.sync-shared" || e.action === "config.sync-shared.complete";
    const write = WRITE_ACTION.test(e.action) && CONTENT_TARGET.test(e.target);
    if (e.action !== "config.autosave" && !sync && !write) continue;
    if (changes.length) { out.push({ id: e.id, actor: e.actor, atMs: e.atMs, changes, ...(e.detail?.undo && typeof e.detail.undo === "object" && Array.isArray((e.detail.undo as { sourceIds?: unknown }).sourceIds) ? { undo: { sourceIds: ((e.detail.undo as { sourceIds: unknown[] }).sourceIds).filter((id): id is number => typeof id === "number"), ...moduleUndoSource(e.detail.undo) } } : {}), truncated: !!e.detail?.truncated }); continue; }
    if (e.detail?.source === "server" && Array.isArray(e.detail.changes)) continue;
    if (e.action === "config.autosave") continue;
    const sourceA0 = typeof e.detail?.sourceA0 === "string" ? e.detail.sourceA0 : undefined;
    const rawVersion = e.detail?.sourceVersion;
    const sourceVersion = typeof rawVersion === "number" && Number.isInteger(rawVersion) && rawVersion > 0 ? rawVersion : undefined;
    out.push({ actor: e.actor, atMs: e.atMs, changes: [], notices: [{ kind: sync ? "sync" : "write", action: e.action, target: e.target, ...(sourceA0 ? { sourceA0 } : {}), ...(sourceVersion ? { sourceVersion } : {}) }] });
  }
  return out;
}

/** Filter before session grouping so unrelated saves do not hide who changed this page. */
export function contentLogForBlocks(items: AuditEntry[], blocks?: readonly string[]) {
  const entries = contentLogEntries(items);
  if (!blocks) return entries;
  return entries.map((e) => ({ ...e, changes: e.changes.filter((c) => blocks.includes(c.block)),
    notices: (e.notices ?? []).filter((n) => blocks.some((b) =>
      b === "talentNodes" && /\/talent-nodes$/.test(n.target)))
  })).filter((e) => e.changes.length || e.notices.length);
}
